import React, { useState, useCallback, useEffect } from "react";
import {
  Form,
  Row,
  Col,
  AutoComplete,
  Input,
  Select,
  TimePicker,
  InputNumber,
  Card,
  Button,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { getCookie } from "../../../helpers/localStorage";
import { ORGANISATONID } from "../../../constants/defaultKeys";
import { searchMedicationApi } from "../../../services/medication.services";
import { debounce } from "../../../helpers/utility";
import {
  dosageTiming,
  isStatMedicine,
  medicineFormOptions,
  medicineFrequency,
  medicineRoutes,
} from "../../../helpers/medicineKeys";
import dayjs from "dayjs";
import useIsMobile from "../../../hooks/useIsMobile";
import useTabletLayout from "../../../hooks/useTabletLayout";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { MedicineOptionRow } from "../../common/MedicineStockTag";
import { dedupeMedicines } from "../../common/medicineOptions";

const { Option } = Select;

const format = "hh A";

const defaultTimes = {
  Once_Daily: ["08 AM"],
  Twice_Daily: ["08 AM", "08 PM"],
  Thrice_Daily: ["08 AM", "01 PM", "08 PM"],
  Four_Times_Daily: ["08 AM", "01 PM", "04 PM", "08 PM"],
};

const setSearchOptions = (data) =>
  dedupeMedicines(data).map((options) => ({
    value: options?.name,
    key: options?.record_id,
    name: options?.name,
    drug: options?.drug1_name,
    // Name on the left, Available / Out of Stock pinned to the right.
    label: <MedicineOptionRow label={options?.name} medicine={options} />,
  }));

const SearchMedicine = ({
  setSelectedId,
  form,
  selectedFrequency,
  setSelectedFrequency,
  initial,
  editData,
  setShowAddMedicine,
}) => {
  const isTablet = useTabletLayout();
  const [data, setData] = useState(null);
  const [searchedText, setSearchedText] = useState("");
  const [times, setTimes] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);

  useEffect(() => {
    if (editData?.id) {
      form.setFieldsValue({
        ...editData,
        name: editData?.medicine_name || editData?.name,
        duration: null,
        comments: "",
        schedule: {
          times: editData?.schedule?.times?.map((time) => dayjs(time, "HH")),
        },
      });
      setSelectedId(editData?.medicine);
      setSelectedFrequency(editData?.frequency);
    }
  }, [editData?.id]);

  const handleSearch = (searchedValue) => {
    searchMedicationApi(
      `query=${searchedValue.toLowerCase()}&organization_id=${getCookie(
        ORGANISATONID
      )}`
    )
      .then((res) => setData(setSearchOptions(res?.data)))
      .catch((err) => console.log(err));
  };

  const optimizedSearch = useCallback(debounce(handleSearch, 300), []);

  const handleSelect = (value, option) => {
    setSelectedId(option?.key);
    form.setFieldsValue({ name: option?.value, drug: option?.drug });
  };

  const isStat = isStatMedicine(selectedFrequency);

  const handleFrequencyChange = (value) => {
    setSelectedFrequency(value);
    const stat = isStatMedicine(value);
    if (stat) {
      setTimes([]);
      form.setFieldsValue({
        schedule: { times: [] },
        duration: undefined,
        days_left_initial: undefined,
      });
      return;
    }
    const defaultTimeValues = defaultTimes[value] || [];
    setTimes(defaultTimeValues.map((time) => dayjs(time, format)));
    form.setFieldsValue({
      schedule: { times: defaultTimeValues.map((time) => dayjs(time, format)) },
    });
  };

  const handleTimeChange = (time, index) => {
    const newTimes = [...times];
    newTimes[index] = time;
    setTimes(newTimes);
    form.setFieldsValue({ schedule: { times: newTimes } });
  };

  const renderTimePickers = () => {
    if (isStat) return null;
    const counts = {
      Once_Daily: 1,
      Twice_Daily: 2,
      Thrice_Daily: 3,
      Four_Times_Daily: 4,
    };
    const numPickers = counts[selectedFrequency] || 0;
    if (!numPickers) return null;

    return (
      <Row gutter={[8, 8]}>
        {Array.from({ length: numPickers }, (_, index) => (
          <Col span={12} key={index}>
            <Form.Item
              name={["schedule", "times", index]}
              rules={[{ required: true, message: "Please select time" }]}
            >
              <TimePicker
                inputReadOnly
                needConfirm={false}
                value={times[index]}
                format={format}
                onChange={(time) => handleTimeChange(time, index)}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Card
      style={{ marginBottom: 10, background: "#FAFAFA" }}
      bodyStyle={{ padding: "16px 20px" }}
    >
      <Row gutter={[12, 0]} align="top" style={{ marginBottom: 8 }}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item
            label="Medicine"
            className="medicine-search"
            name="name"
            rules={[
              {
                required: true,
                message: (
                  <span style={{ color: "red" }}>Please enter medicine</span>
                ),
              },
            ]}
          >
            <AutoComplete
              options={data || []}
              className="search"
              onSearch={optimizedSearch}
              onSelect={handleSelect}
              onChange={(value) => {
                setSearchedText(value);
                optimizedSearch(value);
              }}
              value={searchedText}
              style={{ marginBottom: "6px" }}
              getPopupContainer={
                isTablet ? () => document.body : undefined
              }
              popupClassName={isTablet ? "medicine-search-tablet-dropdown" : undefined}
            >
              <Input
                size="large"
                prefix={<SearchOutlined />}
                placeholder="Search Medicine"
                style={{ width: "100%" }}
                className={inputStyles.mdInput}
              />
            </AutoComplete>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Form"
            name="form"
            rules={[{ required: true, message: "Please choose form" }]}
          >
            <Select
              placeholder="Select Form"
              onChange={(value) => setSelectedForm(value)}
              className={inputStyles.mdSelect}
            >
              {medicineFormOptions?.map((item) => (
                <Option value={item?.value} key={item?.value}>
                  {item?.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {selectedForm === "Others" && (
            <Form.Item
              label="Other Form"
              name="other_form"
              rules={[{ required: true, message: "Please enter form name" }]}
            >
              <Input
                placeholder="Enter other form"
                className={inputStyles.mdInput}
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}
        </Col>

        {/* Frequency */}
        <Col xs={24} sm={12} md={4}>
          <Form.Item
            label="Frequency"
            name="frequency"
            rules={[{ required: true, message: "Please choose frequency" }]}
          >
            <Select
              placeholder="Select Frequency"
              onChange={handleFrequencyChange}
              className={inputStyles.mdSelect}
            >
              {medicineFrequency?.map((item) => (
                <Option value={item?.value} key={item?.value}>
                  {item?.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {/* Timing */}
        <Col xs={24} sm={12} md={3}>
          <Form.Item label="Timing" name="dosage_timing">
            <Select
              allowClear
              placeholder="Select Timing"
              className={inputStyles.mdSelect}
            >
              {dosageTiming?.map((item) => (
                <Option value={item?.value} key={item?.value}>
                  {item?.title}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        {!isStat && (
          <Col xs={24} sm={12} md={3}>
            <Form.Item
              label="Duration"
              name={initial ? "days_left_initial" : "duration"}
              rules={
                !initial
                  ? [
                      {
                        validator: (_, value) => {
                          if (
                            value === undefined ||
                            value === null ||
                            value === ""
                          )
                            return Promise.resolve();
                          if (value < 0)
                            return Promise.reject(
                              "Duration cannot be negative!"
                            );
                          return Promise.resolve();
                        },
                      },
                    ]
                  : []
              }
            >
              <InputNumber
                placeholder={initial ? "Days left" : "Enter Duration"}
                className={inputStyles.mdNumberInput}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        )}

        {/* Route */}
        <Col xs={24} sm={12} md={4}>
          <Form.Item label="Route" name="route">
            <Select
              allowClear
              placeholder="Select"
              className={inputStyles.mdSelect}
            >
              {medicineRoutes?.map((item) => (
                <Option value={item?.value} key={item?.value}>
                  {item?.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[16, 12]}>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="drug">
            <Input
              className={inputStyles.mdInput}
              style={{ width: "100%" }}
              placeholder="Drug"
            />
          </Form.Item>
          <Form.Item name="dose">
            <Input
              placeholder="Dose"
              className={`${inputStyles.mdInput}`}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} md={4}>
          {renderTimePickers()}
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Form.Item name="comments" style={{ marginBottom: 0 }}>
            <Input.TextArea
              placeholder="Comments"
              className={inputStyles.mdInput}
              rows={3}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col
          xs={24}
          sm={12}
          md={24}
          style={{
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <Button
            type="primary"
            onClick={() => form.submit()}
            style={{
              minWidth: 72,
            }}
          >
            Add
          </Button>
          <Button
            onClick={() => {
              setShowAddMedicine(false);
              form.resetFields();
              setSelectedFrequency(null);
            }}
            style={{ minWidth: 72 }}
          >
            Cancel
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default SearchMedicine;
